import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Stack,
    Divider,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Badge as BadgeIcon,
    DirectionsCar as DirectionsCarIcon,
    CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useStore } from '../stores';
import { Driver } from '../stores/DriverStore';
import DashboardLayout from '../components/layout/DashboardLayout/DashboardLayout';
import DriverRegistrationForm from '../components/forms/DriverRegistrationForm';
import { useNavigate } from 'react-router-dom';

const DriverManagement: React.FC = observer(() => {
    const navigate = useNavigate();
    const { driverStore, vehicleStore } = useStore();
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>([]);

    useEffect(() => {
        driverStore.loadDrivers();
        vehicleStore.loadVehicles();
    }, [driverStore, vehicleStore]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredDrivers(driverStore.drivers);
        } else {
            const filtered = driverStore.drivers.filter(driver =>
                driver.user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                driver.license_number.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredDrivers(filtered);
        }
    }, [searchTerm, driverStore.drivers]);

    const handleDriverCreatedSuccess = () => {
        setShowCreateDialog(false);
        setSuccessMessage('Conductor registrado exitosamente');
        setShowSuccessMessage(true);
        driverStore.loadDrivers();
    };

    const handleCloseSnackbar = () => {
        setShowSuccessMessage(false);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, driver: Driver) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedDriver(driver);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        // Don't reset selectedDriver here - it's needed for the modals
    };

    const handleViewDriver = () => {
        setAnchorEl(null); // Close menu
        setShowViewDialog(true);
    };

    const handleEditDriver = () => {
        setAnchorEl(null); // Close menu
        setShowEditDialog(true);
    };

    const handleDeleteDriver = () => {
        setAnchorEl(null); // Close menu
        setShowDeleteDialog(true);
    };

    const confirmDeleteDriver = async () => {
        if (selectedDriver) {
            const success = await driverStore.deleteDriver(selectedDriver.id);
            if (success) {
                setSuccessMessage('Conductor eliminado exitosamente');
                setShowSuccessMessage(true);
                driverStore.loadDrivers();
            }
        }
        setShowDeleteDialog(false);
        setSelectedDriver(null);
    };

    const handleCloseDialogs = () => {
        setShowViewDialog(false);
        setShowEditDialog(false);
        setShowDeleteDialog(false);
        setSelectedDriver(null);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'INACTIVE':
                return 'warning';
            case 'SUSPENDED':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'Activo';
            case 'INACTIVE':
                return 'Inactivo';
            case 'SUSPENDED':
                return 'Suspendido';
            default:
                return status;
        }
    };

    const getVehicleInfo = (vehicleId?: number) => {
        if (!vehicleId) return 'Sin vehículo';
        const vehicle = vehicleStore.vehicles.find(v => v.id === vehicleId);
        return vehicle ? `${vehicle.license_plate} - ${vehicle.brand} ${vehicle.model}` : `Vehículo ID: ${vehicleId}`;
    };

    return (
        <DashboardLayout>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h4" fontWeight="500" mb={1}>
                            Gestión de Conductores
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Administra todos los conductores registrados en el sistema
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/drivers/management')}
                        sx={{
                            backgroundColor: '#1A1A1A',
                            color: 'white',
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: '#333333',
                            },
                        }}
                    >
                        Crear Conductor
                    </Button>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden'
                    }}
                >
                    <Box p={3} borderBottom="1px solid #e0e0e0">
                        <TextField
                            fullWidth
                            placeholder="Buscar conductores por nombre, email, número de licencia ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            variant="outlined"
                            size="medium"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    {driverStore.error && (
                        <Box p={3}>
                            <Alert severity="error">
                                {driverStore.error}
                            </Alert>
                        </Box>
                    )}

                    <TableContainer>
                        {driverStore.isLoading ? (
                            <Box display="flex" justifyContent="center" py={8}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Nombre</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Teléfono</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Licencia</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Estado</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Vehículo</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredDrivers.map((driver) => (
                                        <TableRow
                                            key={driver.id}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: '#f8f9fa'
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography variant="body1" fontWeight={500}>
                                                    {driverStore.getDriverDisplayName(driver)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    @{driver.user.username}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {driver.user.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {driver.user.phone_number}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {driver.license_number}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Vence: {new Date(driver.license_expiry).toLocaleDateString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(driver.status)}
                                                    color={getStatusColor(driver.status) as any}
                                                    size="small"
                                                    sx={{ fontWeight: 500 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {getVehicleInfo(driver.vehicle)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    onClick={(e) => handleMenuOpen(e, driver)}
                                                    size="small"
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}

                        {filteredDrivers.length === 0 && !driverStore.isLoading && (
                            <Box textAlign="center" py={8}>
                                {searchTerm ? (
                                    <Typography variant="body1" color="text.secondary">
                                        No se encontraron conductores con "{searchTerm}"
                                    </Typography>
                                ) : (
                                    <Stack alignItems="center" spacing={2}>
                                        <Typography variant="body1" color="text.secondary">
                                            No hay conductores registrados
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setShowCreateDialog(true)}
                                            sx={{
                                                backgroundColor: '#1A1A1A',
                                                '&:hover': {
                                                    backgroundColor: '#333333',
                                                },
                                            }}
                                        >
                                            Crear Primer Conductor
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </TableContainer>
                </Paper>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        minWidth: 180,
                    }
                }}
            >
                <MenuItem onClick={handleViewDriver} sx={{ py: 1 }}>
                    <VisibilityIcon sx={{ mr: 2, fontSize: 18 }} />
                    <Typography variant="body2">Ver detalles</Typography>
                </MenuItem>
                <MenuItem onClick={handleEditDriver} sx={{ py: 1 }}>
                    <EditIcon sx={{ mr: 2, fontSize: 18 }} />
                    <Typography variant="body2">Editar</Typography>
                </MenuItem>
                <MenuItem onClick={handleDeleteDriver} sx={{ py: 1, color: 'error.main' }}>
                    <DeleteIcon sx={{ mr: 2, fontSize: 18 }} />
                    <Typography variant="body2">Eliminar</Typography>
                </MenuItem>
            </Menu>

            <Dialog
                open={showViewDialog}
                onClose={handleCloseDialogs}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="600">
                            Detalles del Conductor
                        </Typography>
                        <IconButton onClick={handleCloseDialogs} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDriver && (
                        <Box>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: 2
                                }}
                            >
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Avatar
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            mr: 3,
                                            backgroundColor: '#1976d2'
                                        }}
                                    >
                                        <PersonIcon fontSize="large" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" fontWeight="600">
                                            {driverStore.getDriverDisplayName(selectedDriver)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            @{selectedDriver.user.username}
                                        </Typography>
                                        <Box mt={1}>
                                            <Chip
                                                label={getStatusLabel(selectedDriver.status)}
                                                color={getStatusColor(selectedDriver.status) as any}
                                                size="small"
                                                sx={{ fontWeight: 500 }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>

                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h6" fontWeight="600" mb={2} color="primary.main">
                                        Información Personal
                                    </Typography>
                                    <List disablePadding>
                                        <ListItem disablePadding sx={{ mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <EmailIcon color="action" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={selectedDriver.user.email}
                                                secondary="Correo electrónico"
                                            />
                                        </ListItem>
                                        <ListItem disablePadding sx={{ mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <PhoneIcon color="action" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={selectedDriver.user.phone_number}
                                                secondary="Teléfono"
                                            />
                                        </ListItem>
                                    </List>
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography variant="h6" fontWeight="600" mb={2} color="primary.main">
                                        Información de Conductor
                                    </Typography>
                                    <List disablePadding>
                                        <ListItem disablePadding sx={{ mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <BadgeIcon color="action" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={selectedDriver.license_number}
                                                secondary="Número de licencia"
                                            />
                                        </ListItem>
                                        <ListItem disablePadding sx={{ mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CalendarTodayIcon color="action" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={new Date(selectedDriver.license_expiry).toLocaleDateString('es-ES', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                                secondary="Vencimiento de licencia"
                                            />
                                        </ListItem>
                                        <ListItem disablePadding sx={{ mb: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <DirectionsCarIcon color="action" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={getVehicleInfo(selectedDriver.vehicle)}
                                                secondary="Vehículo asignado"
                                            />
                                        </ListItem>
                                    </List>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleCloseDialogs} variant="outlined">
                        Cerrar
                    </Button>
                    <Button
                        onClick={handleEditDriver}
                        variant="contained"
                        startIcon={<EditIcon />}
                        sx={{
                            backgroundColor: '#1A1A1A',
                            '&:hover': {
                                backgroundColor: '#333333',
                            },
                        }}
                    >
                        Editar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showEditDialog}
                onClose={handleCloseDialogs}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="600">
                            Editar Conductor
                        </Typography>
                        <IconButton onClick={handleCloseDialogs} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDriver && (
                        <Box mt={2}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                Formulario de edición en desarrollo. Por ahora puedes ver los detalles del conductor.
                            </Alert>
                            <Typography variant="body1">
                                <strong>Conductor:</strong> {driverStore.getDriverDisplayName(selectedDriver)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                ID: {selectedDriver.id}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleCloseDialogs} variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled
                        sx={{
                            backgroundColor: '#cccccc',
                        }}
                    >
                        Guardar Cambios
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showDeleteDialog}
                onClose={handleCloseDialogs}
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    {selectedDriver && (
                        <>
                            <Typography variant="body1" mb={2}>
                                ¿Estás seguro de que deseas eliminar el conductor <strong>   {driverStore.getDriverDisplayName(selectedDriver)}</strong>?
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Esta acción no se puede deshacer y se eliminará toda la información relacionada con este conductor.
                            </Typography>

                            {vehicleStore.error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {vehicleStore.error}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={handleCloseDialogs}
                        disabled={vehicleStore.isSubmitting}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmDeleteDriver}
                        variant="contained"
                        color="error"
                        disabled={vehicleStore.isSubmitting}
                        sx={{
                            borderRadius: 1.5,
                            px: 3,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        {vehicleStore.isSubmitting ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                Eliminando...
                            </>
                        ) : (
                            'Eliminar'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para crear conductor */}
            <Dialog
                open={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    }
                }}
            >
                <DriverRegistrationForm
                    onSuccess={handleDriverCreatedSuccess}
                />
            </Dialog>

            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    variant="filled"
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
});

export default DriverManagement;
